import React, {useEffect, useRef, useState} from "react";
import {apiAdminCreateNews, apiAdminUpdateNews, apiAdminPublishNews, apiUploadNewsImage} from "../api/news";
import NewsRichTextEditor from "./NewsRichTextEditor";
import ImageUploader from "./ImageUploader";
import NewsAiAssistant from "./NewsAiAssistant";
import {C} from "../styles";
import {usePrompt, useConfirm} from "./FeedbackProvider";

export const NEWS_CATEGORIES = ["Actualité SAPPM", "Instances", "Élections", "Conditions de travail", "Information pratique"];

const fieldStyle = {width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 4, fontSize: 14};
const labelStyle = {fontSize: 13, fontWeight: 600, color: C.text, display: "block", marginTop: 12, marginBottom: 4};

function hasText(node) {
    if (!node) return false;
    if (node.type === "text") return Boolean(node.text && node.text.trim());
    if (Array.isArray(node.content)) return node.content.some(hasText);
    return false;
}

export default function AdminNewsEditor({article, user, onSaved, onCancel}) {
    const prompt = usePrompt();
    const confirm = useConfirm();
    const isAdmin = user?.role === "admin";
    const [dirty, setDirty] = useState(false);
    const [title, setTitle] = useState(article?.title || "");
    const [excerpt, setExcerpt] = useState(article?.excerpt || "");
    const [category, setCategory] = useState(article?.category || "");
    const [cover, setCover] = useState(article?.cover_image_url ? {url: article.cover_image_url, alt: article.cover_image_alt} : null);
    const [externalUrl, setExternalUrl] = useState(article?.external_url || "");
    const [externalLabel, setExternalLabel] = useState(article?.external_link_label || "");
    const [contentJson, setContentJson] = useState(article ? JSON.parse(article.content_json) : {type: "doc", content: [{type: "paragraph"}]});
    const [fieldErrors, setFieldErrors] = useState({});
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);
    // Annuler comme Enregistrer/Publier (via onSaved) démonteraient l'assistant IA en pleine
    // génération : on les bloque le temps de la requête.
    const [aiGenerating, setAiGenerating] = useState(false);
    const richTextEditorRef = useRef(null);

    useEffect(() => {
        setTitle(article?.title || "");
        setExcerpt(article?.excerpt || "");
        setCategory(article?.category || "");
        setCover(article?.cover_image_url ? {url: article.cover_image_url, alt: article.cover_image_alt} : null);
        setExternalUrl(article?.external_url || "");
        setExternalLabel(article?.external_link_label || "");
        setContentJson(article ? JSON.parse(article.content_json) : {type: "doc", content: [{type: "paragraph"}]});
        setFieldErrors({});
        setFormError("");
        setDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [article?.id]);

    useEffect(() => {
        function handleBeforeUnload(e) {
            if (!dirty) return;
            e.preventDefault();
            e.returnValue = "";
        }
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [dirty]);

    const canSubmit = title.trim().length >= 5 && excerpt.trim().length >= 20;
    const submitDisabled = !canSubmit || saving || aiGenerating;

    const hasExistingContent = Boolean(title.trim() || excerpt.trim() || hasText(contentJson));

    function handleAiInsert(draft) {
        setDirty(true);
        setTitle(draft.title || "");
        setExcerpt(draft.excerpt || "");
        setContentJson(draft.contentJson);
        richTextEditorRef.current?.setContent(draft.contentJson);
    }

    function updateField(setter) {
        return (value) => {
            setDirty(true);
            setter(value);
        };
    }

    const handleTitleChange = updateField(setTitle);
    const handleExcerptChange = updateField(setExcerpt);
    const handleCategoryChange = updateField(setCategory);
    const handleCoverChange = updateField(setCover);
    const handleExternalUrlChange = updateField(setExternalUrl);
    const handleExternalLabelChange = updateField(setExternalLabel);
    const handleContentChange = updateField(setContentJson);

    async function handleCancel() {
        if (dirty && !(await confirm("Des modifications n'ont pas été enregistrées. Les abandonner ?", {danger: true}))) return;
        onCancel();
    }

    async function handleImageUploadRequest() {
        return new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/jpeg,image/png,image/webp";
            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return resolve(null);
                const result = await apiUploadNewsImage(file);
                if (result.error || !result.url) return resolve(null);
                const alt = (await prompt("Texte alternatif de l'image (obligatoire)")) || "";
                resolve({url: result.url, alt});
            };
            input.click();
        });
    }

    function buildPayload() {
        return {
            title: title.trim(),
            excerpt: excerpt.trim(),
            category: category || null,
            content_json: JSON.stringify(contentJson),
            cover_image_url: cover?.url || null,
            cover_image_alt: cover?.alt || null,
            external_url: externalUrl.trim() || null,
            external_link_label: externalLabel.trim() || null,
        };
    }

    async function handleSave(publishAfter) {
        setSaving(true);
        setFieldErrors({});
        setFormError("");
        const payload = buildPayload();
        const result = article
            ? await apiAdminUpdateNews(article.id, payload)
            : await apiAdminCreateNews(payload);

        if (result.error) {
            setSaving(false);
            setFieldErrors(result.fields || {});
            setFormError(result.message || result.error);
            return;
        }

        if (publishAfter) {
            const published = await apiAdminPublishNews(result.id);
            setSaving(false);
            if (published.error) {
                setFormError(published.message || "La publication a échoué. Le brouillon a bien été enregistré.");
                return;
            }
            setDirty(false);
            return onSaved(published);
        }

        setSaving(false);
        setDirty(false);
        onSaved(result);
    }

    return (
        <div>
            {isAdmin && (
                <div style={{marginBottom: 16}}>
                    <NewsAiAssistant hasExistingContent={hasExistingContent} onInsert={handleAiInsert} onGeneratingChange={setAiGenerating}/>
                </div>
            )}

            <label style={labelStyle} htmlFor="news-title">Titre</label>
            <input id="news-title" style={fieldStyle} value={title} onChange={e => handleTitleChange(e.target.value)}/>
            {fieldErrors.title && <p style={{color: C.danger, fontSize: 12}}>{fieldErrors.title}</p>}

            <label style={labelStyle} htmlFor="news-excerpt">Résumé</label>
            <textarea id="news-excerpt" style={{...fieldStyle, minHeight: 60}} value={excerpt} onChange={e => handleExcerptChange(e.target.value)}/>
            {fieldErrors.excerpt && <p style={{color: C.danger, fontSize: 12}}>{fieldErrors.excerpt}</p>}

            <label style={labelStyle} htmlFor="news-category">Catégorie</label>
            <select id="news-category" style={fieldStyle} value={category} onChange={e => handleCategoryChange(e.target.value)}>
                <option value="">Aucune</option>
                {NEWS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label style={labelStyle}>Image principale</label>
            <ImageUploader value={cover} onChange={handleCoverChange}/>
            {cover?.url && (
                <>
                    <label style={labelStyle} htmlFor="news-cover-alt">Texte alternatif de l'image</label>
                    <input id="news-cover-alt" style={fieldStyle} value={cover.alt || ""} onChange={e => handleCoverChange({...cover, alt: e.target.value})}/>
                    {fieldErrors.cover_image_alt && <p style={{color: C.danger, fontSize: 12}}>{fieldErrors.cover_image_alt}</p>}
                </>
            )}

            <label style={labelStyle}>Contenu de l'article</label>
            <NewsRichTextEditor key={article?.id ?? "new"} ref={richTextEditorRef} initialContent={contentJson} onChange={handleContentChange} onRequestImageUpload={handleImageUploadRequest}/>
            {fieldErrors.content_json && <p style={{color: C.danger, fontSize: 12}}>{fieldErrors.content_json}</p>}

            <label style={labelStyle} htmlFor="news-external-url">URL externe (optionnel, https uniquement)</label>
            <input id="news-external-url" style={fieldStyle} value={externalUrl} onChange={e => handleExternalUrlChange(e.target.value)}/>
            {fieldErrors.external_url && <p style={{color: C.danger, fontSize: 12}}>{fieldErrors.external_url}</p>}

            <label style={labelStyle} htmlFor="news-external-label">Libellé du bouton externe</label>
            <input id="news-external-label" style={fieldStyle} value={externalLabel} onChange={e => handleExternalLabelChange(e.target.value)}/>
            {fieldErrors.external_link_label && <p style={{color: C.danger, fontSize: 12}}>{fieldErrors.external_link_label}</p>}

            {formError && <p role="alert" style={{color: C.danger, fontSize: 13, marginTop: 12}}>{formError}</p>}

            <div style={{display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap"}}>
                <button type="button" disabled={submitDisabled} onClick={() => handleSave(false)} style={{...styleButton(C), opacity: submitDisabled ? 0.5 : 1}}>
                    {article?.status === "published" ? "Enregistrer les modifications" : "Enregistrer le brouillon"}
                </button>
                {isAdmin && (
                    <button type="button" disabled={submitDisabled} onClick={() => handleSave(true)} style={{...styleButton(C), backgroundColor: C.primary, color: "#fff"}}>
                        Publier
                    </button>
                )}
                <button type="button" disabled={aiGenerating} onClick={handleCancel} style={{...styleButton(C), backgroundColor: "transparent", opacity: aiGenerating ? 0.5 : 1}}>Annuler</button>
            </div>
        </div>
    );
}

function styleButton(C) {
    return {border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontSize: 14};
}
